import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { meetingSchema } from '@/features/calendar/types/meeting';
import { artiaProtectedApi } from '@/lib/api-client';
import { QueryConfig } from '@/lib/react-query';
import { buildQueryString } from '@/utils/filtering-functions';
import { drugName } from '@/utils/drugs';

type DrugMeetingsFilters = {
  tracked?: boolean;
  priority?: boolean;
  timeRange?: 'upcoming' | 'previous';
};

const meetingsSchema = z.array(meetingSchema);

const fetchDrugMeetings = async (drugId: number, filters: DrugMeetingsFilters) => {
  const data = await artiaProtectedApi.get(`drugs/${drugId}/meetings${buildQueryString(filters)}`);

  return meetingsSchema.parse(data);
};

const drugMeetingsQueryOptions = (drugId: number, filters: DrugMeetingsFilters) => {
  return queryOptions({
    queryKey: ['drug-meetings', drugId, { ...filters }],
    queryFn: () => fetchDrugMeetings(drugId, filters)
  });
};

type UseDrugMeetingsQueryOptions = {
  drugId: number;
  filters: DrugMeetingsFilters;
  queryConfig?: QueryConfig<typeof drugMeetingsQueryOptions>
};

export const useDrugMeetings = ({ drugId, filters, queryConfig }: UseDrugMeetingsQueryOptions) => {
  return useQuery({
    ...drugMeetingsQueryOptions(drugId, filters),
    ...queryConfig
  });
};

// Keep the old export for backward compatibility
export const useUpcomingMeetings = useDrugMeetings;

// Export function for meetings CSV
export const exportDrugMeetings = async (drugId: number, filters: DrugMeetingsFilters) => {
  try {
    // First get drug info to create a meaningful filename - same approach as useDrug hook
    const drugResponse = await artiaProtectedApi.get(`drugs/${drugId}`);
    const drug = drugResponse.data || drugResponse; // Handle both wrapped and unwrapped responses
    
    const response = await artiaProtectedApi.post(
      `drugs/${drugId}/meetings/export`,
      {
        drugId,
        priority: filters.priority || false,
        timeRange: filters.timeRange || 'upcoming'
      },
      {
        responseType: 'blob'
      }
    );

    // Create download link - response.data contains the blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Create filename with drug name using the same utility as the UI
    const drugDisplayName = drugName(drug);
    const drugNameForFile = drugDisplayName.replace(/[^a-zA-Z0-9-_]/g, '-').replace(/-+/g, '-');
    const timeRange = filters.timeRange || 'upcoming';
    const filename = `${drugNameForFile}-${timeRange}-meetings-${new Date().toISOString().split('T')[0]}.csv`;
    
    link.setAttribute('download', filename);
    
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Export error:', error);
    // Fallback: export without drug name if drug fetch fails
    const response = await artiaProtectedApi.post(
      `drugs/${drugId}/meetings/export`,
      {
        drugId,
        priority: filters.priority || false,
        timeRange: filters.timeRange || 'upcoming'
      },
      {
        responseType: 'blob'
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    const timeRange = filters.timeRange || 'upcoming';
    const filename = `drug-${drugId}-${timeRange}-meetings-${new Date().toISOString().split('T')[0]}.csv`;
    
    link.setAttribute('download', filename);
    
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};
